// Outbound Caller → Settings → Usage & Billing → Invoices. Lists the selected
// agent's own invoices (its plan and its extra-lead invoices), not the whole
// account's. The page frame is shared with Usage & Billing.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import frame from "../OutboundBilling/OutboundBilling.module.css";
import styles from "./OutboundInvoices.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import { getOutboundInvoices } from "../../Store/apiStore";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";

// Only a paid invoice is a receipt; the rest is money still owed or written off.
const STATUS_LABELS = {
  paid: "Paid",
  open: "Unpaid",
  uncollectible: "Failed",
  void: "Voided",
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(amount);
  } catch {
    return `${(currency || "").toUpperCase()} ${Number(amount).toFixed(2)}`.trim();
  }
};

// Empty state ki graphic: simple grey line-art receipt (sirf stroke, koi fill
// ya rang nahi). Inline SVG, koi network request nahi.
const EmptyInvoicesArt = () => (
  <svg
    className={styles.emptyArt}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#B8BCC8"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Receipt — upar muda kona, neeche zig-zag kinara */}
    <path d="M36 18h38l14 14v68l-6-4-6 4-6-4-6 4-6-4-6 4-6-4-6 4-6-4-4 3V22a4 4 0 0 1 4-4z" />
    <path d="M74 18v14h14" />
    <path d="M46 44h22M46 56h32M46 66h26M46 80h12M68 80h10" />
  </svg>
);

const OutboundInvoices = () => {
  const navigate = useNavigate();
  const agentId = sessionStorage.getItem("SelectAgentId") || "";
  // null = loading, false = failed, otherwise the list.
  const [invoices, setInvoices] = useState(null);
  // Where the next page starts; null = nothing more to load.
  const [nextCursor, setNextCursor] = useState(null);
  // "" = idle, "loading", or "failed" for the Load more button.
  const [more, setMore] = useState("");

  const load = useCallback(() => {
    setInvoices(null);
    setNextCursor(null);
    setMore("");
    getOutboundInvoices(agentId)
      .then((page) => {
        setInvoices(page.invoices);
        setNextCursor(page.nextCursor);
      })
      .catch(() => setInvoices(false));
  }, [agentId]);

  useEffect(load, [load]);

  // Adds the next page under the ones already shown.
  const loadMore = () => {
    setMore("loading");
    getOutboundInvoices(agentId, nextCursor)
      .then((page) => {
        setInvoices((shown) => [...shown, ...page.invoices]);
        setNextCursor(page.nextCursor);
        setMore("");
      })
      .catch(() => setMore("failed"));
  };

  return (
    <div className={frame.page}>
      {/* <div className={frame.brand}>
        <OutboundCallerBrand />
      </div> */}
      <div className={styles.header}>
        <HeaderBar
          title="Outbound Caller"
          subtitle="Invoices"
          backgroundColor="#fff"
          onBack={() => navigate("/outbound-settings")}
        />
      </div>
      {/* <div className={frame.head}>
        <button
          type="button"
          className={frame.back}
          aria-label="Back"
          onClick={() => navigate(-1)}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className={frame.title}>Invoices</h1>
      </div> */}

      <div className={frame.content}>
        {invoices === null && (
          <div role="status" aria-label="Loading invoices">
            <div className={`${frame.skeleton} ${frame.skeletonRow}`} />
            <div className={`${frame.skeleton} ${frame.skeletonRow}`} />
            <div className={`${frame.skeleton} ${frame.skeletonRow}`} />
          </div>
        )}

        {invoices === false && (
          <div className={frame.card}>
            <p className={frame.note}>
              We couldn't load your invoices. Please try again.
            </p>
            <button type="button" className={frame.retry} onClick={load}>
              Retry
            </button>
          </div>
        )}

        {invoices && invoices.length === 0 && !nextCursor && (
          <div className={styles.empty}>
            <EmptyInvoicesArt />
            <h2 className={styles.emptyTitle}>No invoices yet</h2>
            <p className={styles.emptyText}>
              Your first invoice will appear here once you are charged. You can
              download every receipt from this page.
            </p>
            <button
              type="button"
              className={styles.emptyBtn}
              onClick={() => navigate("/outbound-billing")}
            >
              View Usage &amp; Billing
            </button>
          </div>
        )}

        {invoices && invoices.length > 0 && (
          <div className={styles.list}>
            {invoices.map((invoice) => {
              const paid = invoice.status === "paid";
              const unpaid = invoice.status === "open";
              // Stripe's PDF for a receipt; its hosted page is where an unpaid one is paid.
              const link = unpaid
                ? invoice.hostedInvoiceUrl
                : invoice.invoicePdf || invoice.hostedInvoiceUrl;
              return (
                <div key={invoice.id} className={styles.invoice}>
                  <div className={styles.text}>
                    <span className={styles.name}>
                      {invoice.extraLeads
                        ? "Additional lead conversations"
                        : "Outbound Caller Pro"}
                    </span>
                    <span className={styles.meta}>
                      {formatDate(invoice.created)}
                      {invoice.number ? ` · ${invoice.number}` : ""}
                    </span>
                  </div>
                  <div className={styles.side}>
                    <span className={styles.amount}>
                      {formatAmount(invoice.amount, invoice.currency)}
                    </span>
                    <span
                      className={`${styles.status} ${paid ? styles.paid : styles.unpaid}`}
                    >
                      {STATUS_LABELS[invoice.status] || invoice.status}
                    </span>
                  </div>
                  {link && unpaid && (
                    <a
                      className={styles.pay}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Pay
                    </a>
                  )}
                  {link && !unpaid && (
                    <a
                      className={styles.download}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={
                        invoice.number
                          ? `Download invoice ${invoice.number}`
                          : "Download invoice"
                      }
                    >
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M12 4v11m0 0l-4-4m4 4l4-4M5 19h14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {invoices && nextCursor && (
          <div className={styles.more}>
            {more === "failed" && (
              <p className={frame.note}>
                Couldn't load more invoices. Please try again.
              </p>
            )}
            <button
              type="button"
              className={styles.moreBtn}
              disabled={more === "loading"}
              onClick={loadMore}
            >
              {more === "loading" ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      <PounceFooterNav agentId={agentId} active="settings" />
    </div>
  );
};

export default OutboundInvoices;
